import { useAccountContext } from '@/context/account'
import type { Actions } from '@/queries/actions.query'
import { invalidateQueriesGlobally } from '@/utils/queries/invalidateQueriesGlobally'
import type { Network } from '@vaultsfyi/common/types'
import { getChainId } from '@vaultsfyi/common/utils'
import { type Address, type Hex, UserRejectedRequestError } from 'viem'
import { useConfig } from 'wagmi'
import { getCallsStatus, sendCalls, sendTransaction, switchChain, waitForTransactionReceipt } from 'wagmi/actions'
import { useBatchSupported } from './useBatchSupported'

export function useTransaction(
  network: Network,
  actionsData: Actions,
  inputAmount: string,
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
) {
  const wagmiConfig = useConfig()
  const { chainId: connectedChainId } = useAccountContext()
  const { checkBatchSupported } = useBatchSupported(network)

  const txChainId = getChainId(network)
  const currentActionIndex = actionsData?.currentActionIndex ?? 0

  async function executeTx() {
    try {
      if (!actionsData) {
        throw new Error('No actions found')
      }

      if (connectedChainId !== txChainId) {
        await switchChain(wagmiConfig, { chainId: txChainId })
      }

      const { actions } = actionsData

      const isBatchSupported = await checkBatchSupported()

      if (isBatchSupported) {
        try {
          const { id: batchId } = await sendCalls(wagmiConfig, {
            calls: actions.map((action) => ({
              to: action.tx.to as Address,
              data: action.tx.data as Hex | undefined,
              value: action.tx.value ? BigInt(action.tx.value) : undefined,
              chainId: action.tx.chainId,
            })),
          })
          let callsStatus = await getCallsStatus(wagmiConfig, { id: batchId })
          while (callsStatus.status === 'pending') {
            await new Promise((resolve) => setTimeout(resolve, 300))
            callsStatus = await getCallsStatus(wagmiConfig, { id: batchId })
          }
          setTransactionFinished({ txHash: undefined, amount: inputAmount })
        } catch {
          await executeSequential(actions)
        }
      } else {
        await executeSequential(actions)
      }

      await invalidateQueriesGlobally()
    } catch (error) {
      if (
        error instanceof UserRejectedRequestError ||
        (error as { shortMessage?: string })?.shortMessage === 'User rejected the request.'
      ) {
        return
      }
      throw error
    }
  }

  async function executeSequential(actions: NonNullable<typeof actionsData>['actions']) {
    let lastTxHash: string | undefined
    for (let i = currentActionIndex; i < actions.length; i++) {
      const tx = actions[i].tx
      if (!tx) throw new Error(`No transaction found for step ${i}`)

      const hash = await sendTransaction(wagmiConfig, {
        to: tx.to as Address,
        data: tx.data as Hex,
        chainId: tx.chainId,
        value: tx.value ? BigInt(tx.value) : undefined,
      })
      await waitForTransactionReceipt(wagmiConfig, { hash })
      lastTxHash = hash
    }
    setTransactionFinished({ txHash: lastTxHash, amount: inputAmount })
  }

  return { currentActionIndex, executeTx }
}
